import React, { useRef, useMemo, useEffect } from "react";
import { select } from "d3";
import PropTypes from "prop-types";

import {
  CHART_LAYOUT,
  anchorText,
  renderLink,
  renderName,
  revealPath,
  transformFunc,
  transformChart,
  wrapText,
} from "./render";
import { colorScale } from "./Legend";
import { companiesWithNameTooltip, MAIN_COMPANY, tooltips } from "./tooltips";
// import { defaultTooltip } from "./Controls";

export const HierarchicalGraph = ({
  type = "radial",
  root = [],
  partialEdges = [],
  bounds = [{ width: 0, height: 0 }],
  onTooltipSelect = () => {},
  onNodeMouseEnter = () => {},
  onNodeMouseOut = () => {},
  chartOrientation = CHART_LAYOUT.HORIZONTAL,
  highlightedCompanies = [],
  showNameTooltipsForAllCompanies = true,
}) => {
  const ref = useRef(null);
  const linksRef = useRef(null);
  const nodesRef = useRef(null);
  const textRef = useRef(null);

  const { chartTransform, tf, rl, at, renderNameFunc } = useMemo(() => {
    console.log("memoize render functions");
    const chartTransform = transformChart(chartOrientation, bounds);
    const tf = transformFunc(chartOrientation);
    const rl = renderLink(chartOrientation);
    const at = anchorText(chartOrientation, bounds);
    const renderNameFunc = renderName(
      showNameTooltipsForAllCompanies,
      companiesWithNameTooltip,
      chartOrientation
    );

    return { chartTransform, tf, rl, at, renderNameFunc };
  }, [chartOrientation, bounds, showNameTooltipsForAllCompanies]);

  useEffect(() => {
    if (!ref || !linksRef || !nodesRef || !textRef) return;
    if (type !== "radial") return;

    const gLinks = select(linksRef.current);
    const gNodes = select(nodesRef.current);

    ref.current && select(ref).interrupt();
    gLinks.interrupt();
    gNodes.interrupt();

    gLinks
      .attr("stroke-opacity", 1)
      .attr("stroke-width", 3)
      .attr("stroke-miterlimit", 1)
      .selectAll("path")
      .data(
        root.links().filter((d) => d.source.id !== `root`),
        (d) => `${d.source.id} -> ${d.target.id}`
      )
      .join(
        (enter) =>
          enter
            .append("path")
            // .attr("stroke", "url(#gradient-horizontal)")
            .attr("stroke-dasharray", "0,1")
            .attr("opacity", 0)
            .attr("d", rl)
            // .transition()
            // .duration(1000)
            // .delay((d, i) => i * 100)
            // .attr("opacity", 1)
            .call(revealPath)
            .attr("opacity", 1)
            .selection(),
        (update) =>
          update
            // .append("path")
            // .attr("opacity", 1)
            .attr("stroke-dasharray", "0,1")
            .attr("d", rl)
            .call(revealPath)
            .selection(),
        (exit) =>
          exit
            .attr("opacity", 1)
            .transition()
            .duration(200)
            .attr("opacity", 0)
            .remove()
      );

    gNodes
      .selectAll("g")
      .data(
        root.descendants().filter((d) => d.id !== `root`),
        (d) => d.data.name
      )
      .join(
        (enter) =>
          enter
            .append("g")
            .attr("transform", tf)
            .attr("class", "pointer")
            .style("filter", function (d) {
              const { name: company } = select(this).data()[0].data;
              return highlightedCompanies.indexOf(company) >= 0
                ? "url(#white-glow)"
                : null;
            })
            .on("mouseenter", function (e) {
              const {
                name: company,
                incorporation_date: incorporated,
                notes,
                country,
              } = select(this).data()[0].data;
              const curatedText = tooltips[company]?.text;
              const showCuratedTextAfterChapter =
                tooltips[company]?.showAfterChapter || -1;
              onNodeMouseEnter({
                company,
                incorporated,
                notes,
                country,
                curatedText,
                showCuratedTextAfterChapter,
                x: e.clientX,
                y: e.clientY,
              });
            })
            .on("mouseout", onNodeMouseOut)
            .call((enter) =>
              enter
                .append("circle")
                .attr("fill", (d) => colorScale(d.data.country))
                .attr("r", 0)
                .attr("stroke-weight", 2)
                .attr("stroke", "white")
                .transition()
                .duration(1000)
                .delay((d, i) => i * 100)
                .attr("r", (d) => {
                  let r = 5;

                  if (d.data.name === MAIN_COMPANY) return 10;
                  return d.children ? r + d.children.length : r + 2;
                })
                .attr("width", (d) => {
                  let r = 5;

                  if (d.data.name === MAIN_COMPANY) return 10;
                  return d.children ? r + d.children.length : r + 2;
                })
                .selection()
            )
            .call((enter) =>
              enter
                .append("text")
                .attr("text-anchor", at)
                .attr("dx", (d) => {
                  if (!d.children) return 12;
                  return 0;
                })
                .attr("y", (d) => {
                  if (!d.children) return `-.55em`;
                  return `1.5em`;
                }) // TODO make radius dependent
                .text(renderNameFunc)
                .call(wrapText, 100)
                .attr("font-size", 12)
                .attr("class", "bold")
                .attr("fill", "white")
                .attr("background", "black")
                .attr("opacity", 0)
                .transition()
                .duration(1000)
                .delay((d, i) => i * 100)
                .attr("opacity", (d) =>
                  d.data.name === MAIN_COMPANY ? 1 : 0.5
                )
            ),
        (update) =>
          update
            .on("mouseenter", function (e) {
              // this needs to be declared twice because partialEdges changes
              // for some nodes
              // TODO this is hacky and repeats logic
              const {
                name: company,
                incorporation_date: incorporated,
                notes,
                country,
              } = select(this).data()[0].data;
              const curatedText = tooltips[company]?.text;
              const showCuratedTextAfterChapter =
                tooltips[company]?.showAfterChapter || -1;
              onNodeMouseEnter({
                company,
                incorporated,
                notes,
                country,
                curatedText,
                showCuratedTextAfterChapter,
                x: e.clientX,
                y: e.clientY,
              });
            })
            .style("filter", function (d) {
              const { name: company } = select(this).data()[0].data;
              return highlightedCompanies.indexOf(company) >= 0
                ? "url(#white-glow)"
                : null;
            })
            .attr("text-anchor", at)
            .transition()
            .duration(1000)
            .delay((d, i) => i * 100)
            .attr("transform", tf)
            .selection(),
        (exit) =>
          exit
            .attr("opacity", 1)
            .transition()
            .duration(200)
            .attr("opacity", 0)
            .remove()
      );
  }, [
    highlightedCompanies,
    chartTransform,
    tf,
    rl,
    at,
    onTooltipSelect,
    onNodeMouseEnter,
    onNodeMouseOut,
    renderNameFunc,
    partialEdges,
    root,
    type,
    showNameTooltipsForAllCompanies,
  ]);

  return (
    <svg
      ref={ref}
      // viewBox="0 0 100% 100%"
      viewBox={`0 0 ${bounds.width} ${bounds.height}`}
    >
      {/* TODO Doesn't work on straight horizontal or vertical lines, see
        https://stackoverflow.com/questions/21638169/svg-line-with-gradient-stroke-wont-display-straight */}
      <defs>
        <linearGradient id="gradient-horizontal">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="red" stopOpacity=".7" />
        </linearGradient>
        <filter
          id="white-glow"
          x="-5000%"
          y="-5000%"
          width="10000%"
          height="10000%"
        >
          <feFlood
            result="flood"
            floodColor="rgb(55,152,218)"
            floodOpacity="1"
          ></feFlood>
          <feComposite
            in="flood"
            result="mask"
            in2="SourceGraphic"
            operator="in"
          ></feComposite>
          <feMorphology
            in="mask"
            result="dilated"
            operator="dilate"
            radius="2"
          ></feMorphology>
          <feGaussianBlur
            in="dilated"
            result="blurred"
            stdDeviation="5"
          ></feGaussianBlur>
          <feMerge>
            <feMergeNode in="blurred"></feMergeNode>
            <feMergeNode in="SourceGraphic"></feMergeNode>
          </feMerge>
        </filter>
      </defs>
      <g
        // horizontal
        transform={chartTransform}
        // transform={`translate(0, ${bounds.height / 2})`}
      >
        <g fill="none" stroke="#999" ref={linksRef} />
        <g ref={nodesRef} />
        <g ref={textRef} />
      </g>
    </svg>
  );
};

HierarchicalGraph.propTypes = {
  type: PropTypes.oneOf(["radial"]),
};
